import type { ContentEntry } from "./schema";
import { contentEntrySchema } from "./schema";

/**
 * Forme de `corpus-approved.json` quand il est exporté de la base (ADR-0005).
 *
 * Le fichier du dépôt sert à la revue par diff : un export qui réordonnerait
 * les clés ou changerait la mise en forme ferait paraître modifiées des
 * centaines d'entrées intactes. Postgres range les clés d'un `jsonb` à sa
 * façon ; on les remet ici dans l'ordre historique du fichier.
 */

export type ApprovedFileReview = {
  readonly reviewedBy: string;
  readonly reviewedAt: string;
  readonly mode: string;
};

/** Une entrée renvoyée par `exporter_corpus()`, validée et remise dans l'ordre du fichier. */
export function entryFromExport(raw: unknown): ContentEntry {
  const entry = contentEntrySchema.parse(raw);
  return {
    id: entry.id,
    locale: entry.locale,
    answer: entry.answer,
    acceptedAnswers: entry.acceptedAnswers,
    definition: entry.definition,
    culturalNote: entry.culturalNote ?? null,
    ...(entry.revealOrder === undefined ? {} : { revealOrder: entry.revealOrder }),
    difficulty: entry.difficulty,
    editorialStatus: entry.editorialStatus,
    originalityConfirmed: entry.originalityConfirmed,
    draftedBy: entry.draftedBy,
    reviewedBy: entry.reviewedBy,
  };
}

/** Le texte complet de `corpus-approved.json`, fin de ligne comprise. */
export function approvedFileText(entries: readonly ContentEntry[], review: ApprovedFileReview | null): string {
  const notice =
    `Publiable. ${entries.length} entrées approuvées. Export de la table corpus de Supabase (ADR-0005) : ` +
    `ne pas modifier à la main — corriger depuis le site d'administration, puis « pnpm base:exporter ». ` +
    `Définitions rédigées pour LexiReveal, jamais reprises du Wiktionnaire. L'origine assistée par IA reste inscrite sur chaque entrée.`;
  const file = {
    schemaVersion: 1,
    locale: "fr",
    notice,
    // L'approbation en bloc du lot d'origine reste datée et nominative (EDT-013).
    ...(review === null ? {} : { review }),
    entries,
  };
  return `${JSON.stringify(file, null, 2)}\n`;
}

/**
 * Accord entre la base et la réserve des mots classés (ADR-0006).
 *
 * L'export garde les mots réservés dans `corpus-approved.json`, la référence
 * complète — ce fichier n'est plus embarqué : l'application n'importe que
 * `corpus-libre.json`. Mais la réserve, elle, se tient dans
 * `reserve-classee.json`, dont l'ordre est celui des mots du jour. Si la base
 * marque d'autres mots que ce fichier, l'un des deux a dérivé : mieux vaut ne
 * rien écrire que d'embarquer un mot classé ou d'en perdre un.
 *
 * Renvoie la liste des écarts, vide quand tout concorde.
 */
export function ecartsDeReserve(
  exportees: readonly { readonly id: string; readonly reservee?: boolean }[],
  reserve: readonly string[],
): string[] {
  const enBase = new Set(exportees.filter((entree) => entree.reservee === true).map((entree) => entree.id));
  const dansLeFichier = new Set(reserve);
  return [
    ...[...enBase].filter((id) => !dansLeFichier.has(id)).map((id) => `${id} réservé en base, absent de la réserve`),
    ...[...dansLeFichier].filter((id) => !enBase.has(id)).map((id) => `${id} dans la réserve, non réservé ou non publié en base`),
  ];
}

/**
 * Le lot du test G1 après un export (ADR-0005).
 *
 * Il garde ses trente mots — en tirer d'autres changerait l'énigme de testeurs
 * pour une simple retouche —, mais chaque mot y prend l'entrée de l'export :
 * une définition corrigée sur le site y arrive comme ailleurs. Un mot retiré de
 * la base, ou passé dans la réserve des mots classés, quitte le lot : il ne
 * doit plus être joué, et un mot classé jamais embarqué.
 *
 * Remplace `revisions lot`, qui recopiait les révisions tenues dans le dépôt.
 */
export function lotSuivantExport<T extends { readonly id: string }>(
  lot: readonly T[],
  libres: readonly T[],
): { readonly entrees: readonly T[]; readonly sortis: readonly string[] } {
  const parId = new Map(libres.map((entree) => [entree.id, entree]));
  return {
    entrees: lot.flatMap((entree) => {
      const actuelle = parId.get(entree.id);
      return actuelle === undefined ? [] : [actuelle];
    }),
    sortis: lot.filter((entree) => !parId.has(entree.id)).map((entree) => entree.id),
  };
}
